import { Component, OnInit, OnDestroy, ComponentRef, inject } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { Subject, Subscription, lastValueFrom } from 'rxjs';
import { filter, takeUntil, tap, } from 'rxjs/operators';
import { Variables, VariableType } from '~/core/classes/user/variables';
import { GraphService } from 'src/services/graph.service';
import { AppSettingsService } from 'src/services/appsettings.service';
import { AppSettings } from '~/core/models/appsettings';
import { AdminService } from './admin/services/admin.service';
import { adIdRoles } from '../app/core/classes/auth/adIdRoles';
import { NgIf, NgClass, NgComponentOutlet } from '@angular/common';
import { HeaderComponent } from './layout/header/header.component';
import { NavComponent } from './layout/nav/nav.component';
import { FooterComponent } from './layout/footer/footer.component';
import { FeedbackComponent } from './layout/feedback/feedback.component';
import { AccessComponent } from './access/access.component';
import { ErrorComponent } from './error/error.component';
import { SDKLoadingModule } from 'sdk-loading';
import { AlertComponent } from './alert/alert.component';
import { DynamicModule } from 'ng-dynamic-component';
import { CalAngularService, ConfigService, ICvxClaimsPrincipal } from '@cvx/cal-angular';

export interface AdIdRole {
    role_name: string;
    ad_id: string;
    group_name: string;
}

export interface Modal {
    type?: ComponentRef<unknown>;
    inputs?: Record<string, unknown>;
    outputs?: { [event: string]: Function };
}

// Interface to replace AccountInfo from MSAL
export interface UserAccount {
    username?: string;
    name?: string;
    photo?: string;
    firstName?: string;
    lastName?: string;
    initials?: string;
    roles?: string[];
}

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: true,
    imports: [NgIf, HeaderComponent, NavComponent, NgClass, RouterOutlet, FooterComponent, FeedbackComponent, NgComponentOutlet, AccessComponent, ErrorComponent,SDKLoadingModule,AlertComponent,DynamicModule]
})
export class AppComponent implements OnInit, OnDestroy {
    // Stores app-wide settings fetched from the server
    private appSettings: AppSettings = new AppSettings();

    // Public properties for layout and UI state control
    public error: string = "";
    public title: string = "";
    public expand: boolean = false;
    public showNav: boolean = true;
    public showFooter: boolean = true;
    public layoutCssClass: string = "";
    public isLoggedIn: boolean = false;
    public isLoading: boolean = true;
    public adIdRoles: AdIdRole[] = adIdRoles

    // Alert modal controls
    public showAlert: boolean = false;
    public alertTitle: string = "Alert";
    public alertMessage: string = "";
    public alertContinueText: string = "OK";
    public alertCancelText: string = "";

    // Object to dynamically load modal components
    public modal: Modal | undefined;

    // Subject used to manage cleanup of subscriptions
    private readonly _destroying$ = new Subject<void>();
    userName: string | undefined;

    // Initializes services and clears session flags
    constructor(
        private calService: CalAngularService,
        protected configService: ConfigService,
        private graphService: GraphService,
        private router: Router,
        private appSettingsService: AppSettingsService,
        private adminService: AdminService
    ) {
        window.onbeforeunload = null;
        sessionStorage.removeItem("__allowNavigate__");

        // Delay setting layout class until login status is available
        setTimeout(() => {
            if (this.isLoggedIn) {
                this.layoutCssClass = Variables.get("Nav.LayoutCssClass", VariableType.Local) ?? "";
                this.setLayoutClass(false);
            }
        }, 1500);
    }

    // Lifecycle hook: Called once when the component is initialized
    // Loads application settings and starts CAL Angular authentication setup
    public async ngOnInit() {
        await this.appSettingsService.getSettings(true).then(async (appSettings: AppSettings) => {
            if (appSettings) {
                this.appSettings = appSettings;
                this.title = this.appSettings.title;
                this.showNav = this.appSettings.showNav;
                this.showFooter = this.appSettings.showFooter;
                
                // Check authentication status with CAL Angular
                this.calService.isUserSignedIn().subscribe(async (isSignedIn) => {
                    if (isSignedIn) {
                        // Get claims principal for comprehensive user info
                        const claimsPrincipal = this.calService.cvxClaimsPrincipal;
                        if (claimsPrincipal) {
                            this.userName = claimsPrincipal.userName;
                            // Create a UserAccount object from CAL claims principal
                            const userAccount: UserAccount = {
                                username: claimsPrincipal.userName,
                                name: claimsPrincipal.name,
                                photo: "", // Will be set in setUserInfo
                                firstName: undefined, // Will be parsed in setUserInfo
                                lastName: undefined, // Will be parsed in setUserInfo
                                initials: undefined, // Will be generated in setUserInfo
                                roles: claimsPrincipal.roles || []
                            };
                            await this.setAccountInfo(userAccount);
                            
                            // Navigate to home page after authentication and user setup
                            setTimeout(() => {
                                if (this.isLoggedIn && (this.router.url === '/' || this.router.url === '')) {
                                    this.router.navigate(['']);
                                }
                            }, 100);
                        }
                    } else {
                        this.isLoading = false;
                        this.isLoggedIn = false;
                        this.layoutCssClass = "noaccess";
                    }
                });
            }
        });
    }

    // Lifecycle hook: Used to unsubscribe from observables on component destroy
    public ngOnDestroy(): void {
        this._destroying$.next(undefined);
        this._destroying$.complete();
    }

    // Toggles layout expand/collapse state and saves it to local storage
    public setLayoutClass(change: boolean = true) {
        if (change) {
            this.layoutCssClass = this.layoutCssClass !== "" ? "" : "expand";
        }

        if (this.layoutCssClass === "") {
            this.expand = false;
        } else {
            setTimeout(() => {
                this.expand = true;
            }, 100);
        }

        Variables.set("Nav.LayoutCssClass", this.layoutCssClass, VariableType.Local);
    }

    // Reloads the current route (useful for refreshing a component)
    public async reloadPage(page = "") {
        let currentUrl = page === "" ? this.router.url : page;
        this.router.navigate([currentUrl]);
    }

    // Closes the alert modal by continuing (confirm)
    public alertContinue() {
        this.showAlert = false;
    }

    // Closes the alert modal by cancelling (dismiss)
    public alertCancel() {
        this.showAlert = false;
    }

    // Handles role-based access, updates UI state, and saves settings
    private async setAccountInfo(account: UserAccount | null) {
        let allowAccess: boolean = false;
        let roles: string[] = [];

        // Use CAL Angular's isInGroup method to check user roles
        for (let role of this.appSettings.roles) {
            try {
                const isInGroup = await lastValueFrom(this.calService.isInGroup(role));
                if (isInGroup) {
                    roles.push(role);
                }
            } catch (error) {
                console.error(`Error checking group membership for role ${role}:`, error);
            }
        }
        if (roles.length > 0) {
            allowAccess = true;
        }
        if (account) {
            await this.setUserInfo(account, roles);
            await this.appSettingsService.saveSettings(this.appSettings);
        }
        const availableRoles = this.appSettings.roles.filter((role: string) => {
            const normalized = role.toLowerCase();
            return !normalized.includes('power user') && !normalized.includes('admin') && normalized.includes('supplier spend visibility');
        });
        const readUserRole = this.appSettings.roles.filter((role: string) => {
            const normalized = role.toLowerCase();
            return !normalized.includes('supplier spend visibility');
        });
        const readUserRoleString = readUserRole[0];
        let userRoles = this.appSettings?.user?.roles
        const condition = !userRoles?.some((role: any) => availableRoles.includes(role));
        if (userRoles?.includes(readUserRoleString) && condition) {
            const data = {
                "email": this.appSettings.user?.username,
                "group_dict": this.adIdRoles
            }
       this.adminService.getAccessRoles(data).pipe(takeUntil(this._destroying$)).subscribe((val: any) => {
                if (val) {
                    window.location.reload();
                }
                else {
                    // If no reload, continue with access logic
                    if (allowAccess) {
                        this.layoutCssClass = "";
                        this.isLoggedIn = true;
                    } else {
                        this.layoutCssClass = "noaccess";
                        this.isLoggedIn = false;
                    }
                    this.isLoading = false;
                }
            });
        }
        else {
            // If the condition is not met, proceed with access logic immediately
            if (allowAccess) {
                this.layoutCssClass = "";
                this.isLoggedIn = true;
            } else {
                this.layoutCssClass = "noaccess";
                this.isLoggedIn = false;
            }
            this.isLoading = false;
        }
    }

    // Parses user name and roles from CAL account info and stores in settings
    private async setUserInfo(account: UserAccount, roles: string[]) {
        account.photo = "";

        if (account.name) {
            const name: string[] = account.name.split(", ");

            if (name && name.length > 1) {
                const firstName: string = name[1].split(" (")[0].split(" [")[0];
                const lastName: string = name[0];

                account.firstName = firstName;
                account.lastName = lastName;
                account.name = `${firstName} ${lastName}`;
                account.initials = firstName.charAt(0).toUpperCase() + lastName.charAt(0).toUpperCase();
            }
        }

        account.roles = roles;
        this.appSettings.user = account;
    }
}
